import {
  REGISTER_USER,
  LOGIN_USER,
  LOGOUT_USER,
  ERR_USER,
  LOADER_FALSE,
  LOADER_TRUE,
} from "../actionTypes";
import { apiHelper } from "../../utils/apiHelper/apiHelper";
import { toast } from "react-toastify";
import { helperFunction } from "../../utils/helperFunctions";

/**
 * Action creator for registering a new user.
 *
 * @param {Object} user - The user data for registration.
 * @returns {Function} - A function that dispatches actions based on the registration outcome.
 */
export const RegisterUser = (user, navigate) => async (dispatch) => {
  try {
    dispatch({
      type: LOADER_TRUE,
    });
    const res = await apiHelper("post", `/user/sign-up`, user);
    if (res?.data) {
      const { data } = res;
      dispatch({
        type: LOADER_FALSE,
      });
      helperFunction.saveItem("userId", data.data.id);
      dispatch({
        type: REGISTER_USER,
        payload: data,
      });
      toast.success(res?.data?.message);
      navigate("/login");
      return data;
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LOADER_FALSE,
    });
  }
};

/**
 * Action creator for logging in a user.
 *
 * @param {Object} user - The user credentials for login.
 * @returns {Function} - A function that dispatches actions based on the login outcome.
 */
export const LogoutUser = () => (dispatch) => {
  dispatch({ type: LOGOUT_USER });
};

export const LoginUser = (user) => async (dispatch) => {
  try {
    dispatch({
      type: LOADER_TRUE,
    });
    dispatch({
      type: ERR_USER,
      payload: "",
    });
    const res = await apiHelper("post", `/user/log-in`, user);
    if (res?.data) {
      const { data } = res;
      dispatch({
        type: LOADER_FALSE,
      });
      helperFunction.encryptUserData(data?.data, data?.jwtToken);
      dispatch({
        type: LOGIN_USER,
        payload: data,
      });
      return data;
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LOADER_FALSE,
    });
    dispatch({
      type: ERR_USER,
      payload: error?.response?.data?.message,
    });
  }
};

/**
 * Action creator for initiating password recovery.
 *
 * @param {Object} data - The email or username of the user requesting password recovery.
 * @returns {Promise<Object>} - A promise that resolves with the response data if successful.
 */
export const ForgetPassword = async (data) => {
  try {
    // dispatch({
    //     type: LOADER_TRUE
    // })
    const res = await apiHelper("post", `/user/forget-password`, data);
    if (res?.data) {
      // dispatch({
      //     type: LOADER_FALSE
      // })
      const { data } = res;
      return data;
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    // dispatch({
    //     type: LOADER_FALSE
    // })
  }
};

/**
 * Action creator for resetting a user's password.
 *
 * @param {Object} data - The new password data.
 * @returns {Promise<Object>} - A promise that resolves with the response data if successful.
 */
export const ResetPassword = async (data) => {
  try {
    const res = await apiHelper("patch", `/user/reset-password`, data);
    if (res?.data) {
      const { data } = res;
      return data;
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
  }
};

/**
 * Action creator for resending the verification email to a user.
 *
 * @param {Object} data - The user's email or username for verification.
 * @returns {Function} - A function that dispatches actions based on the resend outcome.
 */
export const ResendEmail = (data) => async (dispatch) => {
  try {
    dispatch({
      type: LOADER_TRUE,
    });
    const res = await apiHelper("post", `/user/resend-verify-email`, data);
    if (res?.data) {
      const { data } = res;
      dispatch({
        type: LOADER_FALSE,
      });
      return data;
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LOADER_FALSE,
    });
  }
};

/**
 * Action creator for verifying a user's account.
 *
 * @param {number} userId - The id of the user to be verified.
 * @param {string} token - The verification token.
 *
 * @returns {Function} - A function that dispatches actions based on the verification outcome.
 */
export const verifyAccount = (userId, token, navigate) => async (dispatch) => {
  try {
    dispatch({
      type: LOADER_TRUE,
    });
    const res = await apiHelper(
      "get",
      `/user/verify-token?userId=${userId}&token=${token}`,
    );
    if (res?.data) {
      toast.success(res?.data?.message);
      dispatch({
        type: LOADER_FALSE,
      });
      navigate("/login");
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LOADER_FALSE,
    });
  }
};

/**
 * Action creator for verifying a user's UAE account via OAuth callback.
 *
 * This function sends a request to the server with an authorization code and state
 * to verify the user's UAE account. It handles the server response by dispatching
 * loader actions, encrypting user data, and navigating the user based on the outcome.
 *
 * @param {string} code - The authorization code obtained from UAE Pass.
 * @param {string} state - The state parameter to maintain request state.
 * @param {Function} navigate - Function to navigate to different routes.
 *
 * @returns {Function} - A function that dispatches actions based on the verification outcome.
 */
export const verifyUaeAccount = (code, state, navigate) => async (dispatch) => {
  try {
    dispatch({
      type: LOADER_TRUE,
    });
    const res = await apiHelper(
      "get",
      `/user/auth/callback?code=${code}&state=${state}`,
    );
    if (res?.data) {
      const { data } = res;
      dispatch({
        type: LOADER_FALSE,
      });

      helperFunction.encryptUserData(data?.data, data?.jwtToken);
      return data;
    }
  } catch (error) {
    toast.error(error?.response?.data?.message);
    dispatch({
      type: LOADER_FALSE,
    });
    dispatch({
      type: ERR_USER,
      payload: error?.response?.data?.message,
    });
  }
};
